import type { UIInventoryItem, UIItemSlotRef } from '@sorrem-client/types';
import type { CharacterEquipmentLoadout, Equipment } from '@sorrem/shared';
import {
    canEquipInSlot,
    findFreeCarriedSlot,
    findLoadoutNamedItemConflict,
    getStoredLoadout,
    HANDS_CONFLICT_MESSAGES,
    isBothHanded,
    NAMED_ITEM_EQUIP_CONFLICT_MESSAGES,
    resolveOffHandDisplacement,
    type HandsConflict,
    type HandsItem,
} from '@sorrem/shared';
import { LOADOUT_SLOT_KEYS, LOADOUT_SLOTS, type LoadoutSlotKey } from './loadout-slots';

/** A loadout frame key's flat index in the 7-slot loadout (see `LOADOUT_SLOTS`). */
export function toEquipmentSlotIndex(slotKey: LoadoutSlotKey): number {
    return LOADOUT_SLOT_KEYS.indexOf(slotKey);
}

/** Whether an item of `equipmentType` is a legal drop target for the loadout slot at `slotIndex` (the shared `canEquipInSlot` rule). */
export function canEquipTypeAtSlotIndex(equipmentType: UIInventoryItem['equipmentType'] | undefined, slotIndex: number): boolean {
    const slot = LOADOUT_SLOTS[slotIndex];
    return slot !== undefined && canEquipInSlot(equipmentType, slot.group, slot.groupIndex);
}

/**
 * Loadout slot indices that should highlight as valid drop targets while
 * dragging an item of `equipmentType`. A weapon that takes both hands only
 * goes in the main hand.
 */
export function toHighlightedEquipmentSlotIndices(
    equipmentType: UIInventoryItem['equipmentType'] | undefined,
    takesBothHands = false,
): number[] {
    return LOADOUT_SLOTS.flatMap((slot, index) => (
        canEquipTypeAtSlotIndex(equipmentType, index) && !(takesBothHands && slot.group === 'weapons' && slot.groupIndex > 0) ? [index] : []
    ));
}

/**
 * The equipped items an item card compares `item` with (#341), as its
 * compare badge grades it: like for like, the worn item of its kind in a
 * slot it fits, else any worn item in one; none when every slot it fits is
 * empty. An item that fits either hand, while both hands hold something,
 * compares with both: the main hand first, then the off hand.
 *
 * @param equipmentSlots The equipped items in 7-slot order (see `toEquipmentSlots`).
 */
export function resolveComparedEquipment(
    item: HandsItem & Pick<UIInventoryItem, 'equipmentType'>,
    equipmentSlots: ReadonlyArray<Equipment | null | undefined>,
): Equipment[] {
    const fitting = toHighlightedEquipmentSlotIndices(item.equipmentType, isBothHanded(item)).flatMap((index) => {
        const worn = equipmentSlots[index];
        // A placeholder copy with no catalog id stands for nothing worn.
        return worn && worn.itemDefinitionId.length > 0 ? [{ worn, isHand: LOADOUT_SLOTS[index]?.group === 'weapons' }] : [];
    });
    const hands = fitting.filter((entry) => entry.isHand);
    if (hands.length === 2) {
        return hands.map((entry) => entry.worn);
    }
    const match = fitting.find((entry) => entry.worn.equipmentType === item.equipmentType) ?? fitting[0];
    return match === undefined ? [] : [match.worn];
}

/** Flattens an equipped loadout into the 7-slot order of `LOADOUT_SLOTS`. */
export function toEquipmentSlots(equipped: CharacterEquipmentLoadout): Array<Equipment | null> {
    return LOADOUT_SLOTS.map((slot) => {
        if (slot.group === 'armor') {
            return equipped.armor;
        }
        return (slot.group === 'weapons' ? equipped.weapons[slot.groupIndex] : equipped.miscellaneous[slot.groupIndex]) ?? null;
    });
}

/** The loadout from its 7 flat slots: the inverse of `toEquipmentSlots`. */
export function fromEquipmentSlots(slots: ReadonlyArray<Equipment | null>): CharacterEquipmentLoadout {
    const equipped: CharacterEquipmentLoadout = {
        weapons: [null, null],
        armor: null,
        miscellaneous: [null, null, null, null],
    };
    LOADOUT_SLOTS.forEach((slot, index) => {
        if (slot.group === 'armor') {
            equipped.armor = slots[index] ?? null;
        } else if (slot.group === 'weapons') {
            equipped.weapons[slot.groupIndex] = slots[index] ?? null;
        } else {
            equipped.miscellaneous[slot.groupIndex] = slots[index] ?? null;
        }
    });
    return equipped;
}

/**
 * The slot a move request names: a carried or stored slot as is, a loadout
 * frame (by its flat index) as its group and its index within that group,
 * which is how the server and the host store it.
 */
export function toItemSlotRef(container: UIItemSlotRef['container'], index: number): UIItemSlotRef {
    if (container !== 'equipment') {
        return { container, index };
    }
    const slot = LOADOUT_SLOTS[index];
    if (slot === undefined) {
        throw new RangeError(`No loadout slot ${index}`);
    }
    return { container: 'equipment', group: slot.group, index: slot.groupIndex };
}

type MoveSelection = { itemId: string; container: 'inventory' | 'storage' | 'equipment' };
type MoveTarget = { container: 'inventory' | 'storage' | 'equipment'; index: number };

type NamedRuleItem = Pick<Equipment, 'id' | 'instance'>;
type MovableItem = NamedRuleItem & HandsItem & { readonly equipmentType?: UIInventoryItem['equipmentType'] };

/** What a move is judged against: the character's loadout and the items it can move from. */
export interface ItemMoveContext<TItem extends MovableItem = MovableItem> {
    readonly equipped: CharacterEquipmentLoadout;
    readonly carriedItems: ReadonlyArray<TItem | undefined>;
    /** Stored items, where the page has storage (Character Home); none in a run or at a gathering. */
    readonly storedItems?: ReadonlyArray<TItem | undefined>;
    /** How many carried slots there are, for the off-hand item a two-handed weapon displaces. */
    readonly carriedCapacity: number;
}

/** The items a move takes from: the selection's own container. An empty slot is null in the loadout, undefined in carried and stored slots. */
function sourceItemsOf<TItem extends MovableItem>(context: ItemMoveContext<TItem>, container: MoveSelection['container']): ReadonlyArray<TItem | Equipment | null | undefined> {
    if (container === 'equipment') {
        return toEquipmentSlots(getStoredLoadout(context));
    }
    return container === 'storage' ? context.storedItems ?? [] : context.carriedItems;
}

/** A slot's entry after a move: an item from either kind of container, or empty (null or undefined). */
export type MovedSlot<TItem extends MovableItem> = TItem | Equipment | null | undefined;

/** Every container as a move leaves it, and the hands rule it breaks. */
export interface ItemMoveOutcome<TItem extends MovableItem> {
    /** The 7-slot loadout, in `LOADOUT_SLOTS` order. */
    readonly equipment: ReadonlyArray<MovedSlot<TItem>>;
    readonly carried: ReadonlyArray<MovedSlot<TItem>>;
    readonly stored: ReadonlyArray<MovedSlot<TItem>>;
    /** The hands rule the move breaks (see `resolveOffHandDisplacement`), or null when it keeps them. */
    readonly handsConflict: HandsConflict | null;
}

/**
 * What a move does to every container, the way the host and the server
 * apply it: the item and whatever sits at `target` swap places; then, when
 * a weapon that takes both hands entered the main hand, the off-hand item
 * makes way to the lowest free carried slot (`findFreeCarriedSlot`). Null
 * when the selection isn't found. Character Home's optimistic view and the
 * move rules (`canMoveItem`) both read it, so they agree.
 */
export function applyItemMove<TItem extends MovableItem>(
    context: ItemMoveContext<TItem>,
    selection: MoveSelection,
    target: MoveTarget,
): ItemMoveOutcome<TItem> | null {
    const equipment: Array<MovedSlot<TItem>> = toEquipmentSlots(getStoredLoadout(context));
    const carried: Array<MovedSlot<TItem>> = Array.from({ length: Math.max(context.carriedCapacity, context.carriedItems.length) }, (_, index) => context.carriedItems[index]);
    const stored: Array<MovedSlot<TItem>> = [...context.storedItems ?? []];
    const containers = { equipment, inventory: carried, storage: stored };
    const sourceItems = containers[selection.container];
    const sourceIndex = sourceItems.findIndex((entry) => entry?.id === selection.itemId);
    if (sourceIndex === -1) {
        return null;
    }
    const destinationItems = containers[target.container];
    const sourceEntry = sourceItems[sourceIndex];
    sourceItems[sourceIndex] = destinationItems[target.index];
    destinationItems[target.index] = sourceEntry;

    const mainHandChanged = (target.container === 'equipment' && target.index === 0)
        || (selection.container === 'equipment' && sourceIndex === 0);
    const hands = resolveOffHandDisplacement(equipment[0], equipment[1], mainHandChanged);
    let handsConflict: HandsConflict | null = null;
    if (hands.kind === 'refuse') {
        handsConflict = hands.conflict;
    } else if (hands.kind === 'displace') {
        const free = findFreeCarriedSlot(carried, context.carriedCapacity);
        if (free === undefined) {
            handsConflict = 'no-room-for-off-hand';
        } else {
            carried[free] = hands.item;
            equipment[1] = null;
        }
    }
    return { equipment, carried, stored, handsConflict };
}

/**
 * Whether moving an item would leave the loadout breaking a named item rule
 * (one Empyreal item, no two copies of one named item).
 */
export function hasNamedItemConflict(context: ItemMoveContext, selection: MoveSelection, target: MoveTarget): boolean {
    const outcome = applyItemMove(context, selection, target);
    return outcome !== null && findLoadoutNamedItemConflict(outcome.equipment) !== undefined;
}

/**
 * Whether a move breaks the hands rules (see `resolveOffHandDisplacement`),
 * the way the host and the server judge it: a weapon that takes both hands
 * in the off hand, or anything put beside one, is refused. One that enters
 * the main hand moves the off-hand item to a carried slot, so that is only
 * refused when no carried slot (of `carriedCapacity`) will be free.
 */
export function hasHandsConflict(context: ItemMoveContext, selection: MoveSelection, target: MoveTarget): boolean {
    return (applyItemMove(context, selection, target)?.handsConflict ?? null) !== null;
}

/**
 * Whether an item may move to `target`, the way the server and the host
 * judge it: the named item and hands rules, then for a loadout slot, that
 * the item suits it (and, in a swap within the loadout, that the item it
 * displaces suits the slot it leaves). The run, the gathering and
 * Character Home all ask this.
 */
export function canMoveItem(context: ItemMoveContext, selection: MoveSelection, target: MoveTarget): boolean {
    if (hasNamedItemConflict(context, selection, target) || hasHandsConflict(context, selection, target)) {
        return false;
    }

    if (target.container !== 'equipment') {
        return true;
    }

    const sourceItems = sourceItemsOf(context, selection.container);
    const sourceIndex = sourceItems.findIndex((entry) => entry?.id === selection.itemId);
    if (sourceIndex === -1 || !canEquipTypeAtSlotIndex(sourceItems[sourceIndex]?.equipmentType, target.index)) {
        return false;
    }

    if (selection.container !== 'equipment') {
        return true;
    }

    const destinationEntry = toEquipmentSlots(getStoredLoadout(context))[target.index];
    return !destinationEntry || canEquipTypeAtSlotIndex(destinationEntry.equipmentType, sourceIndex);
}

/**
 * Why a move is refused, in the words the host and the server use for the
 * named item and hands rules, or undefined when it is allowed (`canMoveItem`).
 */
export function findMoveRefusal(context: ItemMoveContext, selection: MoveSelection, target: MoveTarget): string | undefined {
    const outcome = applyItemMove(context, selection, target);
    const namedConflict = outcome === null ? undefined : findLoadoutNamedItemConflict(outcome.equipment);
    if (namedConflict !== undefined) {
        return NAMED_ITEM_EQUIP_CONFLICT_MESSAGES[namedConflict];
    }
    if (outcome?.handsConflict) {
        return HANDS_CONFLICT_MESSAGES[outcome.handsConflict];
    }
    return canMoveItem(context, selection, target) ? undefined : 'It doesn\'t go there.';
}
